import { FormEvent, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { AlertCircle, ArrowRight, Lock, Mail } from 'lucide-react'
import logoSrc from '../assets/logo.png'
import { AuthError, ValidationError, loginWithCredentials, setAuthToken } from '../api/client'

export default function LoginView() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState(() => searchParams.get('email') ?? '')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const destination = useMemo(() => {
    const state = location.state as { from?: { pathname?: string } } | null
    return state?.from?.pathname || '/'
  }, [location.state])

  const canSubmit = email.trim().length > 3 && password.length >= 8 && !loading

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await loginWithCredentials(email.trim(), password)
      setAuthToken(res.token)
      navigate(destination, { replace: true })
    } catch (err) {
      if (err instanceof AuthError) {
        setError('Invalid email or password. Please try again.')
      } else if (err instanceof ValidationError) {
        setError(err.message)
      } else {
        setError(err instanceof Error ? err.message : 'Failed to login.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-surface relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(124,58,237,0.25),transparent_35%),radial-gradient(circle_at_80%_0%,rgba(30,64,175,0.25),transparent_40%)]" />
      <div className="relative min-h-screen grid place-items-center px-4 py-10">
        <div className="w-full max-w-md rounded-2xl border border-surface-border bg-surface-card/90 backdrop-blur-sm p-6 sm:p-8 shadow-2xl">
          <div className="flex items-center gap-2 text-brand-200 mb-4">
            <img src={logoSrc} alt="SocialScale Logo" className="w-5 h-5 object-contain" />
            <span className="font-semibold tracking-tight">SocialScale</span>
          </div>
          <h1 className="text-2xl font-semibold text-white">Welcome back</h1>
          <p className="text-sm text-slate-400 mt-1">
            Sign in to manage campaigns, content workflows, and approvals.
          </p>

          {error && (
            <div className="mt-5 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-300 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <label className="block">
              <span className="text-xs font-medium text-slate-300">Work Email</span>
              <div className="mt-1.5 relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-11 rounded-lg border border-slate-700 bg-slate-900/70 pl-9 pr-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder="you@company.com"
                />
              </div>
            </label>

            <label className="block">
              <span className="text-xs font-medium text-slate-300">Password</span>
              <div className="mt-1.5 relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-11 rounded-lg border border-slate-700 bg-slate-900/70 pl-9 pr-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder="At least 8 characters"
                />
              </div>
            </label>

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full h-11 rounded-lg bg-brand-600 text-white font-medium hover:bg-brand-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              {loading ? 'Signing in...' : 'Sign In'}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>

          <p className="mt-5 text-sm text-slate-400">
            New to SocialScale?{' '}
            <Link to="/register" className="text-brand-300 hover:text-brand-200 font-medium">
              Create an account
            </Link>
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-slate-800 pt-4 text-xs text-slate-500">
            <Link to="/privacy" className="hover:text-slate-300">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-slate-300">
              Terms
            </Link>
            <Link to="/data-deletion" className="hover:text-slate-300">
              Data Deletion
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
